import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import InterviewsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewsNotifications';
import InterviewConsentDetailsContent from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/Pages/InterviewConsentDetails/InterviewConsentDetailsContent';
import useSubjectAddressParams from '@UI/Pages/Beneficiaries/Hooks/Params/useSubjectAddressParams';
import SubjectBreadcrumb from '@UI/Pages/Beneficiaries/Shared/SubjectBreadcrumb';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const containerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

/**
 * Validates the two route segments before mounting `InterviewConsentDetailsContent`: an invalid
 * dossier address redirects to the beneficiary list, an invalid `interview` to that dossier — neither
 * ever reaches the content, which never has to consider a malformed id
 * (`UI/Pages/Interviews/Capture/RecordInterviewPage/index.tsx` follows the same pattern).
 */
const InterviewConsentDetails: React.FC = () => {
	const navigate = useNavigate();
	const params = useParams<{ interview: string }>();
	const { subject } = useSubjectAddressParams();

	const interviewId = params.interview !== undefined ? Number(params.interview) : Number.NaN;
	const isInvalidInterviewId = Number.isNaN(interviewId);

	React.useEffect(() => {
		if (subject === null) {
			navigate(buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY));
			return;
		}

		if (isInvalidInterviewId) {
			navigate(buildSubjectPath(subject));
		}
	}, [subject, isInvalidInterviewId, navigate]);

	const breadcrumbItems = React.useMemo(
		() =>
			subject === null
				? []
				: [
						{ label: 'Entretien', url: `${buildSubjectPath(subject)}/interviews/${interviewId}` },
						{ label: 'Consentement' },
					],
		[subject, interviewId],
	);

	return (
		<div css={containerStyle}>
			{subject !== null && <SubjectBreadcrumb subject={subject} items={breadcrumbItems} />}
			{subject !== null && !isInvalidInterviewId ? (
				<InterviewConsentDetailsContent subject={subject} interviewId={interviewId} />
			) : null}
			<InterviewsNotifications />
		</div>
	);
};

export default InterviewConsentDetails;
